// BeweisSektion — Ebene 3 "Beweise" (Auftrag H Schritt 4, DESIGN-V3-LAGEBILD §3+§4.4).
//
// Progressive Disclosure: BWA-Trend, Forecast+Liqui, Outflow+Mahnstufen,
// Operativ, Risk leben als eingeklappte Sektionen. Der Kopf beantwortet die
// Sektion in EINER Kennzahl + Trendpfeil; der Beweis (Chart/Liste) kommt
// erst auf Klick. Erstbesuch des Tages: alles zu — der Open-State gilt in
// localStorage nur fuer den aktuellen Tag.
//
// Content wird lazy gemountet (erst beim ersten Aufklappen): Sektionen mit
// eigenen Queries (BwaTrend, AnomalyStream, …) feuern sonst schon zu, was
// der 5-Sekunden-Zone Bandbreite klaut.

import { useEffect, useState, type ReactNode } from "react";

export type TrendRichtung = "up" | "down" | "flat";

export interface BeweisTrend {
  richtung: TrendRichtung;
  /** Signal-Ton des Pfeils. Default: gedimmt (Farbe ist Information —
   *  nur faerben, wenn der Trend eine Bewertung traegt). */
  ton?: "ok" | "warn" | "critical";
}

interface Props {
  /** localStorage-Key-Teil, z.B. "bwa". */
  id: string;
  titel: string;
  /** DIE eine Kennzahl im Kopf (num-mono). null/undefined → nur Titel. */
  kennzahl?: string | null;
  trend?: BeweisTrend | null;
  /** Optionaler Detail-Sprung (z.B. Drawer) rechts im Kopf. */
  onDetail?: () => void;
  detailLabel?: string;
  /** AUFTRAG K3: externes Aufklapp-Signal (Zähler; z.B. Kanal-Spülung →
   *  Mahnlauf-Sektion). Jede Erhöhung öffnet die Sektion. */
  erzwingeOffen?: number;
  /** DOM-Anker für scrollIntoView aus Instrumenten-Flows. */
  anchorId?: string;
  children: ReactNode;
}

const ARROW: Record<TrendRichtung, string> = { up: "↑", down: "↓", flat: "→" };

const TON_CLASS: Record<NonNullable<BeweisTrend["ton"]> | "neutral", string> = {
  ok: "text-signal-green",
  warn: "text-signal-amber",
  critical: "text-signal-clay",
  neutral: "text-leitstand-dim",
};

export default function BeweisSektion({
  // `id` bleibt Teil der Props (Aufrufer + BeweisSektion-Identität), wird
  // aber seit M-B5 nicht mehr im Body gebraucht (kein localStorage) →
  // nicht destrukturieren, sonst TS6133.
  titel,
  kennzahl,
  trend,
  onDetail,
  detailLabel = "Detail",
  erzwingeOffen,
  anchorId,
  children,
}: Props) {
  // AUFTRAG M B5: Zustand wird NICHT persistiert — jeden Morgen (jeder
  // Reload) sind alle Beweise zu. Progressive Disclosure ohne Muscle-
  // Memory-Ballast; Aufklappen gilt nur für die aktuelle Sicht.
  const [open, setOpen] = useState(false);
  const [everOpen, setEverOpen] = useState(false);

  // Externes Aufklapp-Signal (Instrumenten-Flow, z.B. Kanal-Spülung).
  useEffect(() => {
    if (erzwingeOffen != null && erzwingeOffen > 0) {
      setOpen(true);
      setEverOpen(true);
    }
  }, [erzwingeOffen]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) setEverOpen(true);
  };

  return (
    <section
      id={anchorId}
      className="rounded-2xl border border-leitstand-hairline bg-leitstand-panel"
    >
      <div className="flex items-center">
        <button
          type="button"
          aria-expanded={open}
          onClick={toggle}
          className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left"
        >
          <span
            aria-hidden
            className={`text-leitstand-dim transition-transform duration-200 motion-reduce:transition-none ${
              open ? "rotate-90" : ""
            }`}
          >
            ›
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-leitstand-text">
            {titel}
          </span>
          {kennzahl != null && (
            <span className="num-mono shrink-0 text-sm text-leitstand-text">{kennzahl}</span>
          )}
          {trend != null && (
            <span aria-hidden className={`shrink-0 ${TON_CLASS[trend.ton ?? "neutral"]}`}>
              {ARROW[trend.richtung]}
            </span>
          )}
        </button>
        {onDetail != null && (
          <button
            type="button"
            onClick={onDetail}
            className="shrink-0 px-3 py-3 text-[11px] text-leitstand-dim hover:text-leitstand-text"
          >
            {detailLabel} →
          </button>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 motion-reduce:transition-none ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          {everOpen && <div className="px-4 pb-4">{children}</div>}
        </div>
      </div>
    </section>
  );
}
